import { useMemo } from "react";

import {
  getCpuEffectiveLimits,
  getGpuEffectiveLimits,
  getTargetQueueName,
  isCpuPartitionName,
} from "../utils/experimentInputValidation";
import { useResourceAggregate } from "./useResourceAggregate";
import { useResourceAggregateByGroups } from "./useResourceAggregateByGroups";

/*
  Hook to fetch queue-slice quota and derive effective resource limits for the
  selected resource group and GPU product.

  accessibleGPUs: {type, available} list of the accessible GPU products
  cpuEffectiveLimits / gpuEffectiveLimits: queue-aware slider caps
*/
export const useResourceAccessLimits = ({
  selectedGroup,
  gpuProduct,
  cpuConfig,
  gpuConfig,
  isSubmitJob,
}) => {
  const { aggregate, loading, error, refetch } = useResourceAggregate({
    enabled: Boolean(selectedGroup),
  });
  const { queueSlices } = useResourceAggregateByGroups({ aggregate });
  const quotaField = isSubmitJob ? "nominal" : "available";

  const cpuQueueQuota = useMemo(() => {
    const queueName = getTargetQueueName(selectedGroup, null, isSubmitJob);
    const slice = queueName
      ? ((isSubmitJob
          ? queueSlices.batch.get(queueName)
          : queueSlices.interactive.get(queueName)) ?? null)
      : null;

    return {
      cpu_millicores: slice?.cpu?.[quotaField] ?? null,
      memory_kibibytes: slice?.memory?.[quotaField] ?? null,
    };
  }, [selectedGroup, isSubmitJob, queueSlices, quotaField]);

  const gpuQueueQuota = useMemo(() => {
    const queueName = getTargetQueueName(
      selectedGroup,
      gpuProduct,
      isSubmitJob,
    );
    const slice = queueName
      ? ((isSubmitJob
          ? queueSlices.batch.get(queueName)
          : queueSlices.interactive.get(queueName)) ?? null)
      : null;

    return {
      cpu_millicores: slice?.cpu?.[quotaField] ?? null,
      memory_kibibytes: slice?.memory?.[quotaField] ?? null,
      gpu_count: slice?.gpu?.[quotaField] ?? null,
    };
  }, [selectedGroup, gpuProduct, isSubmitJob, queueSlices, quotaField]);

  const cpuEffectiveLimits = useMemo(
    () => getCpuEffectiveLimits(selectedGroup, cpuQueueQuota, cpuConfig),
    [selectedGroup, cpuQueueQuota, cpuConfig],
  );

  const gpuEffectiveLimits = useMemo(
    () =>
      getGpuEffectiveLimits(
        selectedGroup,
        gpuProduct,
        gpuQueueQuota,
        gpuConfig,
      ),
    [selectedGroup, gpuProduct, gpuQueueQuota, gpuConfig],
  );

  const accessibleGPUs = useMemo(() => {
    if (!selectedGroup || !aggregate) {
      return [];
    }

    return (selectedGroup.allocations || [])
      .filter((allocation) => !isCpuPartitionName(allocation.partition_name))
      .map((allocation) => {
        const queueName = isSubmitJob
          ? allocation.batch_local_queue
          : allocation.interactive_local_queue;
        const slice = queueName
          ? ((isSubmitJob
              ? queueSlices.batch.get(queueName)
              : queueSlices.interactive.get(queueName)) ?? null)
          : null;

        return {
          type: allocation.partition_name,
          available: slice?.gpu?.available ?? 0,
        };
      });
  }, [selectedGroup, aggregate, isSubmitJob, queueSlices]);

  return {
    accessibleGPUs,
    cpuEffectiveLimits,
    gpuEffectiveLimits,
    loading,
    error,
    refetch,
  };
};
